import { useQuery, useConvexAuth } from 'convex/react';
import { useRef } from 'react';

/**
 * A wrapper around Convex's useQuery that automatically skips the query
 * when the user is not authenticated. This prevents "Unauthenticated" errors
 * from firing during session expiry transitions (e.g., when Clerk token expires
 * but components haven't unmounted yet).
 */
export const useAuthQuery = ((name: any, ...args: any[]) => {
  const { isAuthenticated } = useConvexAuth();
  // If caller already wants to skip, or auth has expired → skip
  const shouldSkip = !isAuthenticated || args[0] === 'skip';
  // Preserve original call signature: useQuery(name) vs useQuery(name, argsObj)
  // Passing undefined as args is NOT the same as omitting them in Convex.
  return shouldSkip
    ? useQuery(name, 'skip')
    : args.length > 0 ? useQuery(name, args[0]) : useQuery(name);
}) as typeof useQuery;

/**
 * Combines useAuthQuery with stable-value semantics: returns the last
 * successfully loaded value while new data is loading (avoids UI flashes).
 * Also auto-skips when unauthenticated.
 */
export const useStableAuthQuery = ((name: any, ...args: any[]) => {
  const { isAuthenticated } = useConvexAuth();
  const shouldSkip = !isAuthenticated || args[0] === 'skip';
  const result = shouldSkip
    ? useQuery(name, 'skip')
    : args.length > 0 ? useQuery(name, args[0]) : useQuery(name);
  const stored = useRef(result);

  if (result !== undefined) {
    stored.current = result;
  }

  return stored.current;
}) as typeof useQuery;
